/**
 * The state a definition's Runs tab shows for one row (its filled badge).
 *
 * A succeeded run with an MR shows the MR's state first (merged or closed,
 * `mr_state`), then its gate (`gate_status.fix_mr_gate`). `no_change` is the
 * backend's reading of sanctum-agent's own markers: a fix run that changed
 * nothing ends `failed` with `NO_CHANGE_FIX_ERROR`, and any other run that
 * wrote nothing succeeds with `changed: false` or `read_only: true`. The
 * harness's only other success pushes `sanctum/<run-id>`: without an MR link
 * (no MR id reported, or none buildable) that run is `pushed`, never
 * `no_change`.
 */

import type { DefinitionRunRow } from '../types/projectPanel.types';

export type RunRowState =
  | 'running'
  | 'gate_red'
  | 'mr_green'
  | 'mr_running'
  | 'mr_open'
  | 'mr_merged'
  | 'mr_closed'
  | 'pushed'
  | 'no_change'
  | 'failed'
  | 'denied'
  | 'stopped'
  | 'expired'
  | 'not_dispatched';

function succeededState(row: DefinitionRunRow): RunRowState {
  if (row.mr_url === null) return row.no_change ? 'no_change' : 'pushed';
  if (row.mr_state === 'merged') return 'mr_merged';
  if (row.mr_state === 'closed') return 'mr_closed';
  switch (row.gate) {
    case 'red':
      return 'gate_red';
    case 'green':
      return 'mr_green';
    case 'running':
      return 'mr_running';
    default:
      return 'mr_open';
  }
}

export function runRowState(row: DefinitionRunRow): RunRowState {
  if (row.kind === 'match') return 'not_dispatched';
  switch (row.status) {
    case 'pending':
    case 'running':
      return 'running';
    case 'succeeded':
      return succeededState(row);
    case 'failed':
      return row.no_change ? 'no_change' : 'failed';
    default:
      return row.status;
  }
}
